import { studentsControllerGetDeepLinksTests } from "@oneapp/data";
import {
  getTestSeriesLabel,
  getVerticalIdFromProfilesData,
  ROUTESWEB,
} from "@repo/utils";
import {
  buildReportUrl,
  navigateGeniusReportWithBackToTestHome,
  persistGeniusStartingSoonExamState,
  isGeniusMissedExam,
} from "@repo/tests-web";
import type { NavigateFunction } from "react-router-dom";
import type { ProfilesData } from "../../../store/useAuthStore";
import {
  GENIUS_OLYMPIAD_ASSESSMENT_LABEL,
  isGeniusAssessmentExamType,
  type GeniusDeeplinkParams,
} from "./parseGeniusDeeplink";
import {
  fetchUpcomingExamFromScheduleSummary,
  isTerminalExamStatus,
  parseGeniusDeepLinkExamContext,
  type GeniusDeepLinkExamContext,
} from "./resolveGeniusDeepLinkExamContext";
import {
  markGeniusDeeplinkExamAttempt,
  markGeniusDeeplinkNavLock,
} from "./geniusDeeplinkNavLock";
import {
  sealGeniusDeeplinkHistory,
  syncGeniusDeeplinkBackTrap,
} from "./geniusDeeplinkBackTrap";
import { shouldEnforceGeniusBackTrap } from "./geniusDeeplinkSession";
import { useAuthStore } from "../../../store/useAuthStore";
import { shouldShowGeniusStartingSoon } from "./shouldShowGeniusStartingSoon";
import { shouldShowGeniusAssessmentNotAvailable } from "./shouldShowGeniusAssessmentNotAvailable";

const ILTS_BASE = `/${ROUTESWEB.APP.TESTS}/ilts`;
const GENIUS_ASSESSMENT_STARTING_SOON_PATH = `/${ROUTESWEB.APP.TESTS}/genius-assessment-starting-soon`;
const GENIUS_ASSESSMENT_NOT_AVAILABLE_PATH = `/${ROUTESWEB.APP.TESTS}/genius-assessment-not-available`;
/** Match ILTS: instructions open 10 minutes before scheduled start. */
const INSTRUCTIONS_BUFFER_MS = 10 * 60 * 1000;

/** Keep Back → logout confirm armed while Genius deeplink nav lock is active. */
function afterGeniusNavigate(): void {
  const profilesData = useAuthStore.getState().profilesData;
  const shouldArm = shouldEnforceGeniusBackTrap(profilesData);
  syncGeniusDeeplinkBackTrap(shouldArm);
  if (!shouldArm) return;
  sealGeniusDeeplinkHistory();
  if (typeof window !== "undefined") {
    window.setTimeout(() => sealGeniusDeeplinkHistory(), 0);
    window.setTimeout(() => sealGeniusDeeplinkHistory(), 50);
  }
}

function isGeniusReportableExamStatus(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return (
    normalized === "attempted" ||
    normalized === "markscalculated" ||
    normalized === "submitted"
  );
}

function resolveDeepLinkApiTestType(examType?: string): string {
  if (isGeniusAssessmentExamType(examType)) return "ilts";
  return (examType ?? "ilts").toLowerCase();
}

function navigateToGeniusAssessmentNotAvailable(
  navigate: NavigateFunction,
  state?: Record<string, unknown>,
): void {
  markGeniusDeeplinkNavLock();
  navigate(GENIUS_ASSESSMENT_NOT_AVAILABLE_PATH, {
    replace: true,
    state: { ...state, fromGeniusDeeplink: true },
  });
  afterGeniusNavigate();
}

function buildGeniusReportFallbackUrl(
  context: GeniusDeepLinkExamContext,
  normalizedTestType: string,
): string {
  const params = new URLSearchParams();
  params.set("src", normalizedTestType);
  params.set("fromViewReport", "true");
  if (context.resolvedExamId) {
    params.set("examId", context.resolvedExamId);
    params.set("examSlotId", context.resolvedExamId);
  }
  if (context.assessmentId) params.set("assessmentId", context.assessmentId);
  if (context.questionPaperId) {
    params.set("questionPaperId", context.questionPaperId);
  }
  if (context.examMode) params.set("examMode", context.examMode);
  if (context.title) params.set("title", context.title);
  if (context.studentStartTime)
    params.set("startTime", context.studentStartTime);
  if (context.studentEndTime) {
    params.set("studentEndTime", context.studentEndTime);
  }
  if (context.detailReportAvailableAfter) {
    params.set(
      "detailReportAvailableAfter",
      context.detailReportAvailableAfter,
    );
  }
  params.set("reportType", "basic");
  return `/${ROUTESWEB.APP.REPORTS}?${params.toString()}`;
}

function navigateToGeniusReport(
  navigate: NavigateFunction,
  context: GeniusDeepLinkExamContext,
  normalizedTestType: string,
  testSeriesLabel: string,
): void {
  markGeniusDeeplinkNavLock();
  markGeniusDeeplinkExamAttempt();

  const reportOptions = {
    examId: context.resolvedExamId,
    assessmentId: context.assessmentId,
    questionPaperId: context.questionPaperId,
    src: normalizedTestType,
    studentEndTime: context.studentEndTime,
    startTime: context.studentStartTime,
    detailReportAvailableAfter: context.detailReportAvailableAfter,
    fromViewReport: true as const,
    examMode: context.examMode,
    title: context.title,
  };

  let url: string;
  try {
    url = buildReportUrl(reportOptions);
  } catch {
    url = buildGeniusReportFallbackUrl(context, normalizedTestType);
  }

  navigateGeniusReportWithBackToTestHome(navigate, url, testSeriesLabel);
  afterGeniusNavigate();
}

function buildBaseExamState(
  context: GeniusDeepLinkExamContext,
  testSeriesLabel: string,
  normalizedTestType: string,
  isGeniusOlympiad: boolean,
) {
  const geniusHomePath = ILTS_BASE;

  return {
    examId: context.resolvedExamId,
    questionPaperId: context.questionPaperId,
    durationMinutes: context.durationMinutes ?? 0,
    title: context.title,
    instructions: context.instructions,
    startTime: context.startTime,
    examMode: context.examMode ?? "",
    answerSheetUploadDurationMinutes: context.answerSheetUploadDurationMinutes,
    detailReportAvailableAfter: context.detailReportAvailableAfter,
    currentPage: testSeriesLabel,
    pathToNav: isGeniusOlympiad
      ? geniusHomePath
      : normalizedTestType === "ilts"
        ? ILTS_BASE
        : `/${ROUTESWEB.APP.TESTS}/${normalizedTestType}`,
  };
}

function isFutureGeniusStartTime(startTime?: string): boolean {
  if (!startTime) return false;
  const startMs = new Date(startTime).getTime();
  if (Number.isNaN(startMs)) return false;
  return startMs - Date.now() > INSTRUCTIONS_BUFFER_MS;
}

async function resolveGeniusExamContext(
  params: GeniusDeeplinkParams,
  normalizedTestType: string,
): Promise<GeniusDeepLinkExamContext | null> {
  const { scheduleId, cohortAsmId, examId } = params;
  if (!scheduleId || !examId) return null;

  let context: GeniusDeepLinkExamContext | null = null;

  try {
    const response = await studentsControllerGetDeepLinksTests(
      {
        testType: normalizedTestType,
        scheduleId,
        examId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(cohortAsmId ? { cohortAsmId } : {}),
      } as Parameters<typeof studentsControllerGetDeepLinksTests>[0],
      { clientName: "oneApp" },
    );

    context = parseGeniusDeepLinkExamContext(response, examId);
  } catch (error) {
    console.warn("[GeniusDeeplink] getDeepLinksTests failed:", error);
  }

  const needsScheduleFallback =
    !isTerminalExamStatus(context?.examStatus) &&
    (!context?.startTime ||
      !context.examMode ||
      !context.durationMinutes ||
      shouldShowGeniusStartingSoon(
        context?.examStatus ?? "",
        context?.startTime,
        context?.endTime,
      ));

  if (needsScheduleFallback) {
    const scheduleContext = await fetchUpcomingExamFromScheduleSummary(
      scheduleId,
      examId,
    );
    if (scheduleContext) {
      context = {
        ...(context ?? scheduleContext),
        ...scheduleContext,
        questionPaperId:
          context?.questionPaperId || scheduleContext.questionPaperId,
        examMode: context?.examMode ?? scheduleContext.examMode,
        durationMinutes:
          context?.durationMinutes ?? scheduleContext.durationMinutes,
        instructions: context?.instructions || scheduleContext.instructions,
        title: context?.title || scheduleContext.title,
        startTime: scheduleContext.startTime ?? context?.startTime,
        endTime: scheduleContext.endTime ?? context?.endTime,
        examStatus: isTerminalExamStatus(context?.examStatus)
          ? (context?.examStatus ?? scheduleContext.examStatus)
          : scheduleContext.examStatus || context?.examStatus || "coming_up",
      };
    }
  }

  return context;
}

export async function navigateToGeniusDeepLinkTest(
  params: GeniusDeeplinkParams,
  navigate: NavigateFunction,
  profilesData: ProfilesData | null,
): Promise<void> {
  const { scheduleId, examId, examType } = params;
  const isGeniusOlympiad = isGeniusAssessmentExamType(examType);
  const apiTestType = resolveDeepLinkApiTestType(examType);

  if (!scheduleId || !examId) {
    if (isGeniusOlympiad) {
      navigateToGeniusAssessmentNotAvailable(navigate);
      return;
    }
    navigate(`/${ROUTESWEB.APP.TESTS}`, { replace: true });
    return;
  }

  const testSeriesLabel = isGeniusOlympiad
    ? GENIUS_OLYMPIAD_ASSESSMENT_LABEL
    : getTestSeriesLabel(getVerticalIdFromProfilesData(profilesData));
  const normalizedTestType = apiTestType;

  try {
    const context = await resolveGeniusExamContext(params, normalizedTestType);

    if (isGeniusOlympiad && shouldShowGeniusAssessmentNotAvailable(context)) {
      navigateToGeniusAssessmentNotAvailable(
        navigate,
        context
          ? buildBaseExamState(
              context,
              testSeriesLabel,
              normalizedTestType,
              isGeniusOlympiad,
            )
          : undefined,
      );
      return;
    }

    if (!context) {
      // Genius olympiad must never land on /tests/ilts — URL lock sends that to blank.
      if (isGeniusOlympiad) {
        navigateToGeniusAssessmentNotAvailable(navigate);
        return;
      }
      navigate(
        normalizedTestType === "ilts" ? ILTS_BASE : `/${ROUTESWEB.APP.TESTS}`,
        { replace: true },
      );
      return;
    }

    const baseExamState = buildBaseExamState(
      context,
      testSeriesLabel,
      normalizedTestType,
      isGeniusOlympiad,
    );

    const navigateToStartingSoon = () => {
      const startingSoonState = {
        ...baseExamState,
        fromGeniusDeeplink: true as const,
      };
      persistGeniusStartingSoonExamState(startingSoonState);
      markGeniusDeeplinkNavLock();
      navigate(GENIUS_ASSESSMENT_STARTING_SOON_PATH, {
        replace: true,
        state: startingSoonState,
      });
      afterGeniusNavigate();
    };

    const { examStatus, studentStartTime, studentEndTime } = context;

    if (isGeniusReportableExamStatus(examStatus)) {
      navigateToGeniusReport(
        navigate,
        context,
        normalizedTestType,
        testSeriesLabel,
      );
      return;
    }

    if (isGeniusOlympiad && isFutureGeniusStartTime(context.startTime)) {
      navigateToStartingSoon();
      return;
    }

    if (!context.examMode || !context.durationMinutes) {
      if (isGeniusOlympiad) {
        navigateToGeniusAssessmentNotAvailable(navigate, baseExamState);
        return;
      }
      navigate(
        normalizedTestType === "ilts" ? ILTS_BASE : `/${ROUTESWEB.APP.TESTS}`,
        { replace: true },
      );
      return;
    }

    if (examStatus === "in_progress" || examStatus === "started") {
      if (isGeniusOlympiad) {
        markGeniusDeeplinkNavLock();
      }
      navigate(`/${ROUTESWEB.APP.TESTS}/exam`, {
        replace: true,
        state: {
          ...baseExamState,
          durationMinutes: context.durationMinutes,
          examMode: context.examMode,
          isResume: examStatus === "in_progress",
          examStartedAt: studentStartTime,
          examEndTime: studentEndTime,
          fromGeniusDeeplink: true,
        },
      });
      afterGeniusNavigate();
      return;
    }

    if (isGeniusMissedExam(context)) {
      if (isGeniusOlympiad) {
        navigateToGeniusAssessmentNotAvailable(navigate, baseExamState);
        return;
      }
      navigate(`/${ROUTESWEB.APP.TESTS}/solutions`, {
        replace: true,
        state: {
          examId: context.resolvedExamId,
          questionPaperId: context.questionPaperId,
          title: context.title,
          currentPage: testSeriesLabel,
          pathToNav: isGeniusOlympiad
            ? `${ILTS_BASE}?tab=missed`
            : normalizedTestType === "ilts"
              ? `${ILTS_BASE}?tab=missed`
              : `/${ROUTESWEB.APP.TESTS}/${normalizedTestType}`,
        },
      });
      afterGeniusNavigate();
      return;
    }

    if (isGeniusOlympiad && isFutureGeniusStartTime(context.startTime)) {
      navigateToStartingSoon();
      return;
    }

    if (isGeniusOlympiad) {
      markGeniusDeeplinkNavLock();
    }
    navigate(`/${ROUTESWEB.APP.TESTS}/general-instructions`, {
      replace: true,
      state: {
        ...baseExamState,
        durationMinutes: context.durationMinutes,
        examMode: context.examMode,
        fromGeniusDeeplink: true,
      },
    });
    afterGeniusNavigate();
  } catch (error) {
    console.error("[GeniusDeeplink] Failed to resolve test deeplink:", error);
    if (isGeniusOlympiad) {
      navigateToGeniusAssessmentNotAvailable(navigate);
      return;
    }
    navigate(
      normalizedTestType === "ilts" ? ILTS_BASE : `/${ROUTESWEB.APP.TESTS}`,
      { replace: true },
    );
  }
}
